import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Printer, RotateCcw } from "lucide-react";
import { FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { CHECKLIST, RURAL_REVIEW_DATE } from "@/data/rural";

const STORE_KEY = "pfn_rural_checklist";

const RuralChecklist = () => {
  const [done, setDone] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(done));
  }, [done]);

  const all = CHECKLIST.flatMap((g) => g.items);
  const doneCount = all.filter((_, i) => done[i]).length;
  const mandatoryCount = all.filter((x) => x.mandatory).length;
  const mandatoryDone = all.filter((x, i) => x.mandatory && done[i]).length;

  return (
    <div data-testid="rural-checklist-page">
      <Seo title="Hipcamp Readiness Checklist" description="The printable NSW rural hosting readiness checklist — planning, safety, environmental, sanitation, business and guest management, mandatory vs recommended." />
      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/rural" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> Rural Land
          </Link>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-teal tracking-tight">Hipcamp Readiness Checklist</h1>
          <p className="mt-3 text-charcoal/70 leading-relaxed max-w-2xl">
            Work through it at your own pace — your progress saves on this device. Print it for the
            fridge, and note the difference between legal requirements and good practice.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <div className="text-sm" data-testid="checklist-progress">
              <span className="font-serif text-2xl text-teal">{doneCount}</span>
              <span className="text-charcoal/60"> / {all.length} complete</span>
              <span className="ml-3 text-xs font-mono uppercase tracking-[0.12em] text-charcoal/55">
                Mandatory: {mandatoryDone}/{mandatoryCount}
              </span>
            </div>
            <button
              onClick={() => window.print()}
              data-testid="checklist-print"
              className="inline-flex items-center gap-2 bg-teal text-ivory text-xs font-mono uppercase tracking-[0.12em] px-4 py-2.5 rounded-full hover:bg-teal-dark transition-colors"
            >
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
            <button
              onClick={() => setDone({})}
              data-testid="checklist-reset"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.12em] text-charcoal/60 hover:text-teal transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {CHECKLIST.map((g, gi) => (
            <FadeIn key={g.group} delay={gi * 0.03}>
              <div className="bg-white border border-cloud" data-testid={`checklist-group-${gi}`}>
                <div className="px-6 py-4 border-b border-cloud">
                  <h2 className="font-serif text-xl text-teal">{g.group}</h2>
                </div>
                <ul className="divide-y divide-cloud">
                  {g.items.map((item) => {
                    const idx = CHECKLIST.slice(0, gi).reduce((n, x) => n + x.items.length, 0) + g.items.indexOf(item);
                    return (
                      <li key={idx}>
                        <button
                          onClick={() => setDone((d) => ({ ...d, [idx]: !d[idx] }))}
                          data-testid={`checklist-item-${idx}`}
                          className="w-full flex items-start gap-4 px-6 py-4 text-left hover:bg-sand-light/40 transition-colors"
                        >
                          <span
                            className={`mt-0.5 w-5 h-5 border shrink-0 flex items-center justify-center transition-colors ${
                              done[idx] ? "bg-teal border-teal" : "border-cloud bg-white"
                            }`}
                          >
                            {done[idx] && <Check className="w-3.5 h-3.5 text-ivory" />}
                          </span>
                          <span className={`text-sm leading-relaxed flex-1 ${done[idx] ? "text-charcoal/45 line-through" : "text-charcoal/85"}`}>
                            {item.text}
                          </span>
                          <span
                            className={`shrink-0 text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-1 ${
                              item.mandatory ? "bg-teal text-ivory" : "bg-sand-light text-charcoal/60"
                            }`}
                          >
                            {item.mandatory ? "Legal requirement" : "Recommended"}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </FadeIn>
          ))}
          <p className="text-xs text-charcoal/55 leading-relaxed max-w-2xl">
            "Legal requirement" marks items typically requiring an approval, standard or licence —
            your council confirms which apply to your property. This checklist is educational, not
            legal advice. Reviewed {RURAL_REVIEW_DATE}.
          </p>
        </div>
      </section>
    </div>
  );
};

export default RuralChecklist;
